import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AlertModal from '@components/AlertModal/AlertModal';
import { PaperlessEnrollmentProps } from '@components/types';
import { selectHasPaperless, selectHasRefreshed } from '@store/selectors/widgets.selectors';
import { ModalAlert } from '@components/ModalAlert/ModalAlert';
import { AmplifyLogs, cmsNameToID, UIConstants } from '@constants';
import { selectContent } from '@store/selectors/cmsContent.selector';
import { fetchPaperlessAsync, shownPaperlessSuccess } from '@store/actions/paperless.action';
import { selectPaperlessState } from '@store/selectors/paperless.selector';
import { fetchWidgetRefreshAsync } from '@store/actions/widgets.actions';
import Loader from '@components/Loader/Loader';
import { logUserEvent } from '@analytics/amplifyLogging';
import { Tile, TileContextProvider } from '@fmc-digital/ui';
import { Paperless } from '@utils/tiles.data';
import { ThemeProvider } from 'styled-components';
import { defaultTheme } from '@theme';
import { PaperlessModal } from '@components_v2/UI/atoms/paperless-modal/Paperless';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackBarProps } from '@hooks/useSnackbarProps';

const PaperlessEnrollment: React.FC<PaperlessEnrollmentProps> = ({ selectedAccount }) => {
    const dispatch = useDispatch();

    const queryClient = useQueryClient();
    const paperlessContent: string = useSelector(selectContent(cmsNameToID.paperless));
    const hasPaperless = useSelector(selectHasPaperless);
    const paperlessData = useSelector(selectPaperlessState);
    const isEligibleForPaperless = hasPaperless?.initParam?.find(({ value }) => value === selectedAccount?.accountNumber);
    const hasRefreshed = useSelector(selectHasRefreshed);
    const { enqueueSnackbar } = useSnackbar();

    const { confirmationTitle, confirmationHeading, enrollNow, close, errorTitle, ok, enrollErrorDetails, closeIconRed } = UIConstants;
    const [enroll, setEnroll] = useState(false);
    const [enrollSuccess, setEnrollSuccess] = useState(false);
    const [enrollError, setEnrollError] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleEnrollNow = () => {
        const attributes = {
            ...AmplifyLogs.paperlessEnrolment
        };
        logUserEvent(attributes);
        setEnroll(!enroll);
    };
    const handleButtonClick = (type: string) => {
        console.log(type);
        if (type.toUpperCase() === close) {
            setEnroll(false);
            setEnrollSuccess(false);
            setEnrollError(false);
            dispatch(fetchWidgetRefreshAsync.request('?refresh=true'));
            setShowLoader(true);
        } else if (type === enrollNow) {
            setShowLoader(true);
            dispatch(fetchPaperlessAsync.request(selectedAccount?.accountNumber));
            setEnroll(false);
        } else if (type === ok) {
            setEnrollError(false);
        } else if (type === 'close_modal' && enrollSuccess) {
            setEnroll(false);
            setEnrollSuccess(false);
            setEnrollError(false);
            dispatch(fetchWidgetRefreshAsync.request('?refresh=true'));
            setShowLoader(true);
        } else if (type === 'closeEnroll' || type === 'close_modal') {
            setEnroll(false);
            setEnrollSuccess(false);
            setEnrollError(false);
        }
    };

    useEffect(() => {
        if (paperlessContent) {
            document.getElementById('enrollPaperless')?.addEventListener('click', handleEnrollNow);
        }
        if (hasRefreshed) {
            setShowLoader(false);
        }
    }, [paperlessContent, hasRefreshed, handleEnrollNow]);
    useEffect(() => {
        if (paperlessData?.success) {
            setShowLoader(false);
            setEnrollSuccess(true);
        }
        if (paperlessData?.hasError) {
            setShowLoader(false);
            setEnrollError(true);
        }
    }, [paperlessData]);
    const errorSnackBar = useSnackBarProps({ severity: 'error', duration: 3000 });
    const successSnackBar = useSnackBarProps({ severity: 'success', duration: 3000 });
    useEffect(() => {
        if (enrollSuccess && !paperlessData?.shownPaperlessSnack) {
            enqueueSnackbar(
                'Your request change has been submitted. The changes will go into effect within the next 24 hours.',
                successSnackBar
            );
            handleButtonClick('close_modal');
            dispatch(shownPaperlessSuccess(true));
            queryClient.invalidateQueries(['widgets']);
        } else if (enrollError && !paperlessData?.shownPaperlessSnack) {
            enqueueSnackbar(enrollErrorDetails, errorSnackBar);
            handleButtonClick(ok);
            dispatch(shownPaperlessSuccess(true));
        }
    }, [
        enrollSuccess,
        enrollError,
        paperlessData?.shownPaperlessSnack,
        enqueueSnackbar,
        handleButtonClick,
        dispatch,
        queryClient,
        enrollErrorDetails,
        ok
    ]);
    return (
        <ThemeProvider theme={defaultTheme}>
            <Loader showLoader={showLoader} />

            {isEligibleForPaperless && (
                <TileContextProvider
                    {...Paperless}
                    onDeepLink={handleEnrollNow}
                    logUserEvent={event => console.log(event)}
                    key={`paperless_tile_template_provider`}
                >
                    <Tile key={`paperless_template_tile`} />
                </TileContextProvider>
            )}
            <PaperlessModal
                open={enroll}
                onEnroll={(type: string) => handleButtonClick(type)}
                title={enroll ? `${confirmationHeading}  ${selectedAccount?.accountNumber}` : confirmationTitle}
            />
            {enrollError && (
                <AlertModal title={errorTitle} handleClose={ele => handleButtonClick(ele)}>
                    <ModalAlert
                        data-testid="enrollErrorButton"
                        details={enrollErrorDetails}
                        buttonText={ok}
                        image={closeIconRed}
                        handleClose={ele => handleButtonClick(ele)}
                    />
                </AlertModal>
            )}
        </ThemeProvider>
    );
};

export default React.memo(PaperlessEnrollment);
